import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';

export interface Student {
    id: string;
    firstName: string;
    lastName: string;
    chineseName?: string;
    gender?: string;
    age?: number;
    avatar?: string;
    parentFirstName?: string;
    parentLastName?: string;
    parentPhone?: string;
    email?: string;
    userId?: string;
}

export interface Course {
    id: string;
    name: string;
    nameEn?: string;
}

interface UseStudentsListResult {
    students: Student[];
    course: Course | null;
    loading: boolean;
    error: string | null;
}

export const useStudentsList = (courseId: string | null, programId?: string | null): UseStudentsListResult => {
    const { t } = useTranslation();
    const [students, setStudents] = useState<Student[]>([]);
    const [course, setCourse] = useState<Course | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 获取课程详情
    useEffect(() => {
        const fetchCourseDetails = async () => {
            if (!courseId) {
                setError(t('course.idMissing'));
                setLoading(false);
                return;
            }

            try {
                const courseDoc = await getDoc(doc(db, 'courses', courseId));
                if (courseDoc.exists()) {
                    const courseData = courseDoc.data();
                    setCourse({
                        id: courseDoc.id,
                        name: courseData.name || '',
                        nameEn: courseData.nameEn || ''
                    });
                } else {
                    console.error('课程不存在:', courseId);
                    setError(t('course.notFound'));
                }
            } catch (err) {
                console.error('获取课程详情失败:', err);
                setError(t('course.fetchError'));
            }
        };

        fetchCourseDetails();
    }, [courseId, t]);

    // 获取学生列表
    useEffect(() => {
        const fetchStudents = async () => {
            if (!courseId) {
                console.warn('未提供课程ID');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                console.log('开始获取课程学生列表, 课程ID:', courseId);

                // 创建课程引用
                const courseRef = doc(db, 'courses', courseId);

                // 修改查询：使用 array-contains 操作符来查询courses数组中包含指定课程的注册记录
                let registrationsQuery = query(
                    collection(db, 'registrations'),
                    where('courses', 'array-contains', courseRef),
                    where('status', '==', 'approved')
                );

                // 如果指定了项目，则额外按项目过滤，避免跨项目（如不同年份）共用同一课程时混入其他项目的学生
                if (programId) {
                    const programRef = doc(db, 'programs', programId);
                    registrationsQuery = query(registrationsQuery, where('program', '==', programRef));
                }

                console.log('执行注册查询...');
                const querySnapshot = await getDocs(registrationsQuery);
                console.log(`找到 ${querySnapshot.size} 条注册记录`);

                if (querySnapshot.empty) {
                    setStudents([]);
                    setLoading(false);
                    return;
                }

                // 收集所有注册记录数据和用户引用
                const registrationsData = [];
                const userRefs = new Map(); // 使用 Map 存储用户引用，避免重复

                for (const docSnapshot of querySnapshot.docs) {
                    const data = docSnapshot.data();
                    if (!data) continue;

                    // 存储注册记录数据
                    registrationsData.push({
                        id: docSnapshot.id,
                        data: data
                    });

                    // 收集用户引用
                    if (data.user) {
                        const userPath = data.user.path;
                        userRefs.set(userPath, data.user);
                    }
                }

                console.log(`收集了 ${userRefs.size} 个不同的用户引用`);

                // 一次性批量获取所有用户信息
                const userDocs = new Map();
                if (userRefs.size > 0) {
                    const userPromises = Array.from(userRefs.values()).map(userRef => getDoc(userRef));
                    const userSnapshots = await Promise.all(userPromises);

                    userSnapshots.forEach(userDoc => {
                        if (userDoc.exists()) {
                            userDocs.set(userDoc.ref.path, {
                                id: userDoc.id,
                                data: userDoc.data()
                            });
                        }
                    });

                    console.log(`成功获取 ${userDocs.size} 个用户信息`);
                }

                // 处理所有注册记录，构建学生列表
                const studentsData = registrationsData.map(registration => {
                    try {
                        const { id, data } = registration;

                        // 获取用户信息（如果有）
                        let avatar = '';
                        let userId = '';

                        if (data.user) {
                            const userPath = data.user.path;
                            const userInfo = userDocs.get(userPath);

                            if (userInfo) {
                                avatar = userInfo.data.avatar || '';
                                userId = userInfo.id;
                            }
                        }

                        // 计算年龄
                        let age = 0;
                        if (data.dateOfBirth) {
                            const birthDate = data.dateOfBirth.toDate();
                            const today = new Date();
                            age = today.getFullYear() - birthDate.getFullYear();
                            const m = today.getMonth() - birthDate.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                age--;
                            }
                        }

                        return {
                            id: id,
                            firstName: data.firstName || data.studentName || '',
                            lastName: data.lastName || '',
                            chineseName: data.chineseName || '',
                            gender: data.gender || '',
                            age: age,
                            avatar: avatar,
                            parentFirstName: data.parentFirstName || (data.parentName ? data.parentName.split(' ')[0] : ''),
                            parentLastName: data.parentLastName || (data.parentName ? data.parentName.split(' ')[1] : ''),
                            parentPhone: data.phone || '',
                            email: data.email || '',
                            userId: userId
                        };
                    } catch (itemErr) {
                        console.error(`处理注册记录 ${registration.id} 时出错:`, itemErr);
                        return null;
                    }
                }).filter(Boolean) as Student[];

                // 按名字排序
                studentsData.sort((a, b) => a.firstName.localeCompare(b.firstName));

                console.log(`成功处理 ${studentsData.length} 名学生信息`);
                console.log({studentsData});
                setStudents(studentsData);
            } catch (err) {
                console.error('获取学生列表失败:', err);
                setError(t('student.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchStudents();
    }, [courseId, programId, t]);
    // console.log({students});
    return { students, course, loading, error };
};

// 工具函数
export const getGenderText = (gender: string, t: any) => {
    switch (gender) {
        case 'male':
            return t('student.male');
        case 'female':
            return t('student.female');
        default:
            return t('student.other');
    }
};

/**
 * 导出学生列表为CSV文件
 * @param students 学生列表数据
 * @param courseName 课程名称（可选）
 * @param t 翻译函数
 */
export const exportStudentsToCSV = (students: Student[], courseName?: string, t?: any) => {
    if (!students || students.length === 0) {
        console.error('没有学生数据可导出');
        return;
    }
    
    // 定义CSV头部
    const headers = [
        'Last Name', 'First Name', 'Chinese Name', 'Gender', 'Age', 'Parent Last Name', 'Parent First Name', 'Parent Phone', 'Email'
    ];
    
    // 转换数据为CSV行
    const rows = students.map(student => [
        student.lastName || '',
        student.firstName || '',
        student.chineseName || '',
        t ? getGenderText(student.gender || '', t) : student.gender || '',
        student.age ? student.age.toString() : '',
        student.parentLastName || '',
        student.parentFirstName || '',
        student.parentPhone || '',
        student.email || ''
    ]);
    
    // 添加头部行
    rows.unshift(headers);
    
    // 转换为CSV格式
    const csvContent = rows.map(row => 
        row.map(cell => {
            // 如果单元格包含逗号、双引号或换行符，则使用双引号包裹并处理内部的双引号
            if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
        }).join(',')
    ).join('\n');
    
    // 创建Blob对象
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // 创建下载链接
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `StudentsList${courseName ? `-${courseName}` : ''}-${new Date().toISOString().split('T')[0]}.csv`);
    
    // 模拟点击下载
    document.body.appendChild(link);
    link.click();
    
    // 清理
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
